import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Reveal, MaskLines } from "@/components/Reveal";
import { SectionLabel, CTAGroup } from "@/components/bits";
import { IMAGES, PARTNERS, SERVICES, AUDIENCES, PROJECTS, CONTACT, TAGLINE } from "@/lib/site";
import { ArrowUpRight, ArrowRight } from "lucide-react";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] w-full overflow-hidden grain" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.heroMansion} alt="Illuminated luxury residence at dusk" className="h-full w-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-isav-bg" />
      <div className="absolute inset-0 bg-gradient-to-t from-isav-bg via-isav-bg/20 to-isav-bg/40" />

      <div className="relative z-10 mx-auto flex h-full max-w-[1600px] flex-col justify-end px-6 pb-16 md:px-12 md:pb-24">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 1 }}>
          <SectionLabel>Santa Barbara · Los Angeles · Orange County · Palm Desert</SectionLabel>
        </motion.div>

        <h1 className="mt-8 font-serif text-[13vw] font-300 leading-[0.92] tracking-tight md:text-[8.5vw] lg:text-[7.5vw]">
          <MaskLines lines={["The home,", "perfectly"]} startDelay={0.25} />
          <MaskLines lines={["orchestrated."]} startDelay={0.5} lineClassName="italic accent-text" />
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.75, duration: 0.9 }}
          className="mt-6 font-serif text-2xl font-300 italic text-white/70 md:text-3xl"
        >
          {TAGLINE}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-10 flex flex-col gap-10 md:flex-row md:items-end md:justify-between"
        >
          <p className="max-w-md font-sans text-base font-300 leading-relaxed text-white/70 md:text-lg">
            Home automation, architectural lighting &amp; window treatments, and custom home theater — integrated into the world's finest residences.
          </p>
          <CTAGroup />
        </motion.div>
      </div>

      <div className="absolute bottom-6 right-6 z-10 hidden md:right-12 md:block">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Scroll</span>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="relative bg-isav-bg py-28 md:py-40" data-testid="manifesto">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal>
          <SectionLabel>Who we build for</SectionLabel>
          <h2 className="mt-8 max-w-4xl font-serif text-4xl font-300 leading-[1.05] md:text-6xl">
            One partner for the homeowner, the builder, and the designer.
          </h2>
        </Reveal>

        <div className="mt-20 grid grid-cols-1 gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-3">
          {AUDIENCES.map((a, i) => (
            <Reveal key={a.key} delay={i * 0.1} className="group bg-isav-bg p-10 transition-colors duration-500 hover:bg-isav-bg2 md:p-12">
              <span className="font-mono text-sm text-isav-green">{a.index}</span>
              <h3 className="mt-8 font-serif text-3xl font-400">{a.title}</h3>
              <p className="mt-5 text-sm leading-relaxed text-white/55">{a.body}</p>
              <div className="mt-10 h-px w-full bg-white/10 transition-all duration-500 group-hover:[background:linear-gradient(to_right,#3b82f6,#10b981)]" />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedServices() {
  const featured = SERVICES.filter((s) => s.primary);
  return (
    <section className="relative bg-isav-bg2 py-28 md:py-40" data-testid="featured-services">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <SectionLabel>What we design</SectionLabel>
            <h2 className="mt-8 max-w-2xl font-serif text-4xl font-300 leading-[1.05] md:text-6xl">
              Signature disciplines.
            </h2>
          </div>
          <Link to="/services" className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-white" data-testid="view-all-services">
            All capabilities <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-12">
          {featured.map((s, i) => (
            <Reveal
              key={s.slug}
              delay={i * 0.08}
              className={i === 0 ? "lg:col-span-7" : "lg:col-span-5"}
            >
              <Link to={`/services#${s.slug}`} data-testid={`service-card-${s.slug}`} className="group block">
                <div className="hover-media relative aspect-[4/3] overflow-hidden border border-white/10">
                  <img src={IMAGES[s.image]} alt={s.title} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-isav-bg via-transparent to-transparent" />
                  <span className="absolute left-6 top-6 font-mono text-sm text-white/70">{s.index}</span>
                  <ArrowUpRight className="absolute right-6 top-6 h-6 w-6 text-white opacity-0 transition-all duration-500 group-hover:opacity-100" />
                </div>
                <div className="mt-6">
                  <h3 className="font-serif text-3xl font-400">{s.title}</h3>
                  <p className="mt-2 text-sm italic text-white/50">{s.tagline}</p>
                  <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/55">{s.summary}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Partners() {
  return (
    <section className="relative overflow-hidden border-y border-white/10 bg-isav-bg py-20" data-testid="partners">
      <div className="mx-auto mb-12 max-w-[1600px] px-6 md:px-12">
        <SectionLabel>Technology partners</SectionLabel>
      </div>
      <div className="marquee-fade">
        <Marquee speed={38} gradient={false} autoFill>
          {PARTNERS.concat(PARTNERS).map((p, i) => (
            <span key={i} className="mx-12 font-serif text-5xl font-300 text-white/30 transition-colors duration-500 hover:text-white md:text-7xl">
              {p}
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
}

function Gallery() {
  const items = PROJECTS.slice(0, 6);
  return (
    <section className="relative bg-isav-bg py-28 md:py-40" data-testid="home-gallery">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <SectionLabel>Selected work</SectionLabel>
            <h2 className="mt-8 font-serif text-4xl font-300 leading-[1.05] md:text-6xl">Recent projects.</h2>
          </div>
          <Link to="/portfolio" className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-white" data-testid="view-portfolio">
            Full portfolio <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
          {items.map((p, i) => (
            <Reveal key={p.title} delay={(i % 3) * 0.08} className={i % 5 === 0 ? "md:row-span-2" : ""}>
              <div className="hover-media group relative h-full overflow-hidden border border-white/10">
                <img src={IMAGES[p.image]} alt={p.title} className={`w-full object-cover ${i % 5 === 0 ? "h-full min-h-[400px]" : "aspect-[4/3]"}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-isav-bg/90 via-transparent to-transparent opacity-70 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-isav-green">{p.county}</p>
                  <h3 className="mt-2 font-serif text-2xl font-400">{p.title}</h3>
                  <p className="text-sm text-white/55">{p.discipline}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceAreas() {
  return (
    <section className="relative bg-isav-bg2 py-28 md:py-40" data-testid="service-areas">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal>
          <SectionLabel>Where we work</SectionLabel>
          <h2 className="mt-8 max-w-3xl font-serif text-4xl font-300 leading-[1.05] md:text-6xl">
            Serving the California coast's most discerning homes.
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-4">
          {CONTACT.serviceAreas.map((area, i) => (
            <Reveal key={area} delay={i * 0.1} className="bg-isav-bg2 p-10 md:p-12">
              <span className="font-mono text-sm text-isav-green">0{i + 1}</span>
              <h3 className="mt-6 font-serif text-3xl font-400">{area}</h3>
              <p className="mt-4 text-sm leading-relaxed text-white/50">
                Design, integration, and white-glove service for luxury residences and estates throughout {area.replace(" County", "")}.
              </p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ClosingCTA() {
  return (
    <section className="relative overflow-hidden bg-isav-bg py-28 md:py-40" data-testid="closing-cta">
      <div className="absolute inset-0 opacity-30">
        <img src={IMAGES.livingNight} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-isav-bg/80" />
      </div>
      <div className="relative mx-auto max-w-[1600px] px-6 text-center md:px-12">
        <Reveal>
          <h2 className="mx-auto max-w-4xl font-serif text-5xl font-300 leading-[1.02] md:text-7xl">
            Let's design the home that <span className="italic accent-text">anticipates you</span>.
          </h2>
          <div className="mt-12 flex justify-center">
            <CTAGroup className="justify-center" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="page-home">
      <Hero />
      <Manifesto />
      <FeaturedServices />
      <Partners />
      <Gallery />
      <ServiceAreas />
      <ClosingCTA />
    </div>
  );
}
